/**
 * Larovi Plus (1.21, W6 first step – docs/preismodell.md). One place that decides what a family
 * has, used by the app (badges, hints, settings) and by the server (every Plus function checks
 * there, not only in the app).
 *
 * - `plan: 'testfamily'` – invited test family: everything free while the test runs.
 * - `plan: 'plus'` – Plus switched on (admin for now; payment comes in its own release).
 * - `plan: 'free'` – free on purpose, trial over or switched off (admin: "Plus aus", to test).
 * - no plan – free, with 30 days of Plus to get to know it (Mike, 14.10.: "Ja."). The trial is
 *   computed, not stored: from the day the family was created, or from the day Plus started for
 *   families that were there before – nothing to migrate, nothing a client could change.
 */

const DAY = 86_400_000;

/** Days of Plus every family gets to get to know it, without payment details. */
export const TRIAL_DAYS = 30;

/** Plus starts with release 1.21 – families from before get their trial from this day on. */
export const PLUS_START = Date.UTC(2026, 9, 16); // 2026-10-16

/** Recipes a free family can keep adding (Mike, 14.10.: "20 Rezepte frei"). */
export const FREE_RECIPES = 20;

/** Warn this many days before the trial ends (and again on its last day). */
export const TRIAL_WARN_DAYS = 3;

export type PlanKind = 'free' | 'trial' | 'plus' | 'testfamily';

export interface PlanState {
  kind: PlanKind;
  /** Plus functions are open: trial, Plus and test families. */
  plus: boolean;
  /** Trial only: when it ends (ms) and full days left, counting today (≥ 1 while it runs). */
  trialEnds?: number;
  trialDaysLeft?: number;
}

/** What a family has right now. `household` needs only these fields (any extra is ignored). */
export function planOf(
  household: { plan?: string | null; createdAt?: number | null },
  now: number = Date.now(),
): PlanState {
  if (household.plan === 'testfamily') return { kind: 'testfamily', plus: true };
  if (household.plan === 'plus') return { kind: 'plus', plus: true };
  if (household.plan === 'free') return { kind: 'free', plus: false };
  const trialEnds = trialEndOf(household.createdAt ?? now);
  if (now < trialEnds) {
    return {
      kind: 'trial',
      plus: true,
      trialEnds,
      // never more than the 30 promised days (before Plus starts the end is further away)
      trialDaysLeft: Math.min(TRIAL_DAYS, Math.max(1, Math.ceil((trialEnds - now) / DAY))),
    };
  }
  return { kind: 'free', plus: false, trialEnds };
}

/** End of the trial for a family created at `createdAt`. */
export const trialEndOf = (createdAt: number) => Math.max(createdAt, PLUS_START) + TRIAL_DAYS * DAY;

/** Show the calm "Probemonat endet bald" hint: the last few days of the trial. */
export const trialEndingSoon = (p: PlanState) =>
  p.kind === 'trial' && (p.trialDaysLeft ?? 99) <= TRIAL_WARN_DAYS;

/**
 * May a family add one more recipe? Over the limit nothing is lost – the recipes stay readable,
 * cookable and editable; only new ones wait (Mike, 15.10.: "passt alles so").
 */
export const canAddRecipe = (p: PlanState, recipeCount: number) =>
  p.plus || recipeCount < FREE_RECIPES;
